import { MutableRefObject, useMemo, useState } from 'react';
import { useResizeDetector as underlyingUseResizeDetector } from 'react-resize-detector';

type UnderlyingHook = typeof underlyingUseResizeDetector;

// Patches useResizeDetector to solve a bug where an Observer might not be set up
// if the underlying ref remains null while useResizeDetector is initialized.
//
// see https://github.com/maslianok/react-resize-detector/blob/v6.6.5/src/useResizeDetector.ts
//
// useResizeDetector tracks element size using a ref and sets up the resize observer
// in an effect hook.  if ref.current holds null when the effect hook runs, no
// resize will be setup (this is correct).  However, if the ref gets assigned so that
// ref.current is not null, yet no other props change, the effect hook won't be run again
// and so no observer will be set up.  This issue is caused by ref.current being used in
// useResizeDetector's effect hook.  Refs don't trigger re-renders, simply changing ref.current
// will not update the component.
//
// This workaround uses useResizeDetector's targetRef.  Rather than pass a ref, we
// pass a state value that looks like a ref.  When the underlying ref changes, we
// change the state value, thereby kicking react-resize-detector's useResizeDetector
// to run again.
export default function useResizeDetector(
  ...args: Parameters<UnderlyingHook>
): ReturnType<UnderlyingHook> {
  const [props] = args;
  const [element, setElement] = useState(null);
  const refLike = useMemo(() => ({ current: element }), [element]);

  const { ref, ...restResult } = underlyingUseResizeDetector({
    targetRef: refLike,
    ...props,
  });

  return {
    ref: (setElement as unknown) as MutableRefObject<null>,
    ...restResult,
  };
}
